import type { StudioSystemType } from 'types/global'
import type { HardwareDetailType } from '../types'
import { ISystemHardwareDetail, SystemDesignComponentDetailSnapshot } from './systemDesignComponentDetailSnapshot'

// Mode types for reconciliation
export type ReconciliationMode = 'union' | 'diff' | 'system'

// Main reconciler class
export class SystemHardwareReconciler implements ISystemHardwareDetail {
  private _modules?: HardwareDetailType[]
  private _inverters?: HardwareDetailType[]
  private _batteries?: HardwareDetailType[]
  private _others?: HardwareDetailType[]
  private _system: SystemDesignComponentDetailSnapshot
  private _referenceSystem: SystemDesignComponentDetailSnapshot
  private _mode: ReconciliationMode

  constructor({
    system,
    referenceSystem,
    mode,
  }: {
    system: SystemDesignComponentDetailSnapshot
    referenceSystem: SystemDesignComponentDetailSnapshot
    mode: ReconciliationMode
  }) {
    this._system = system
    this._referenceSystem = referenceSystem
    this._mode = mode
  }

  get modules(): HardwareDetailType[] {
    if (this._modules) return this._modules
    this._modules = SystemHardwareReconciler.getReconciledComponentDetails({
      componentDetails: this._system.modules,
      referenceComponentDetails: this._referenceSystem.modules,
      mode: this._mode,
    })
    return this._modules
  }

  get inverters(): HardwareDetailType[] {
    if (this._inverters) return this._inverters
    this._inverters = SystemHardwareReconciler.getReconciledComponentDetails({
      componentDetails: this._system.inverters,
      referenceComponentDetails: this._referenceSystem.inverters,
      mode: this._mode,
    })
    return this._inverters
  }

  get batteries(): HardwareDetailType[] {
    if (this._batteries) return this._batteries
    this._batteries = SystemHardwareReconciler.getReconciledComponentDetails({
      componentDetails: this._system.batteries,
      referenceComponentDetails: this._referenceSystem.batteries,
      mode: this._mode,
    })
    return this._batteries
  }

  get others(): HardwareDetailType[] {
    if (this._others) return this._others
    this._others = SystemHardwareReconciler.getReconciledComponentDetails({
      componentDetails: this._system.others,
      referenceComponentDetails: this._referenceSystem.others,
      mode: this._mode,
    })
    return this._others
  }

  // Static utility methods
  static componentDetailListToMap<T extends HardwareDetailType>(items: T[]): Record<string, T> {
    const result: Record<string, T> = {}

    for (const component of items) {
      const key = `${component.type}:${component.activated_id}:${component.code}`

      if (key in result) {
        result[key] = {
          ...result[key],
          quantity: result[key].quantity + component.quantity,
        }
      } else {
        result[key] = { ...component }
      }
    }

    return result
  }

  static getReconciliatedComponentDetailsBySystems = ({
    system,
    referenceSystem,
    mode,
  }: {
    system: StudioSystemType
    referenceSystem: StudioSystemType
    mode: ReconciliationMode
  }): HardwareDetailType[] => {
    const currentHardwareComponents = new SystemDesignComponentDetailSnapshot(system).allSystemHardware
    const referenceHardwareComponents = new SystemDesignComponentDetailSnapshot(referenceSystem).allSystemHardware
    return SystemHardwareReconciler.getReconciledComponentDetails({
      componentDetails: currentHardwareComponents,
      referenceComponentDetails: referenceHardwareComponents,
      mode,
    })
  }

  static getReconciledComponentDetails<T extends HardwareDetailType>({
    componentDetails,
    referenceComponentDetails,
    mode,
  }: {
    componentDetails: T[]
    referenceComponentDetails: T[]
    mode: ReconciliationMode
  }): T[] {
    if (mode === 'system') {
      return componentDetails
    }

    const currentComponentMap = SystemHardwareReconciler.componentDetailListToMap(componentDetails)
    const referenceComponentMap = SystemHardwareReconciler.componentDetailListToMap(referenceComponentDetails)
    const keys = new Set([...Object.keys(currentComponentMap), ...Object.keys(referenceComponentMap)])

    if (mode === 'diff') {
      const result: T[] = []

      for (const key of keys) {
        const system = currentComponentMap[key]
        const referenceSystem = referenceComponentMap[key]
        const currentQuantity = system?.quantity || 0
        const referenceQuantity = referenceSystem?.quantity || 0
        const delta = currentQuantity - referenceQuantity

        if (delta !== 0) {
          const base = { ...(referenceSystem || system) }
          base.quantity = delta
          result.push(base as T)
        }
      }

      return result
    }

    if (mode === 'union') {
      const result: Record<string, T> = {}

      // Add system components
      for (const [key, component] of Object.entries(currentComponentMap)) {
        result[key] = { ...component }
      }

      // Add or merge referenceSystem components
      for (const [key, component] of Object.entries(referenceComponentMap)) {
        if (key in result) {
          result[key] = {
            ...result[key],
            quantity: result[key].quantity + component.quantity,
          }
        } else {
          result[key] = { ...component }
        }
      }

      return Object.values(result)
    }

    return []
  }
}

export default SystemHardwareReconciler
